import { type CSSProperties, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb } from '../adapters/powersync';
import { useSyncStatus } from './useSyncStatus';
import { retrySync } from './syncActions';
import { recordRetryLog } from './syncHealth';
import { createNeedHelpCase } from './syncNeedHelp';
import { getUploadQueueStats } from './useUploadQueueStats';

/**
 * PRD.253 §3 — Global stuck-sync modal.
 *
 * Rendered from the authenticated app shell so it can surface on ANY page (not
 * only Sync Status). Appears when there are pending uploads AND sync is not
 * healthy — either `stuck` (online but failing) or `offline` (no network).
 * Offers Retry Sync, Ask Support for Help, Open Sync Status, and Dismiss for
 * now. While offline, Ask Support is disabled (the request can't reach the
 * server). Force Resync is intentionally NOT offered here (PRD §5) —
 * destructive recovery stays on the Sync Status page.
 *
 * Dismissal is stored in sessionStorage so it does not nag for the rest of the
 * tab session but returns on a fresh session if the device is still stuck.
 */

const ACK_KEY = 'powercare_stuck_sync_modal_ack';
const SYNC_STATUS_PATH = '/clinic/sync-status';

function isAcked(): boolean {
  try {
    return sessionStorage.getItem(ACK_KEY) === 'true';
  } catch {
    return false;
  }
}

function setAcked(): void {
  try {
    sessionStorage.setItem(ACK_KEY, 'true');
  } catch {
    // ignore — sessionStorage unavailable
  }
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--sp-4, 16px)',
};

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 460,
  background: 'var(--bg-surface, #fff)',
  borderRadius: 'var(--radius-lg, 14px)',
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
  padding: 'var(--sp-6, 24px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4, 16px)',
};

const primaryBtn: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 'var(--radius-md, 10px)',
  border: 'none',
  background: 'var(--primary, #2563eb)',
  color: '#fff',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-semi, 600)',
  cursor: 'pointer',
};

const secondaryBtn: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  background: 'var(--bg-surface, #fff)',
  color: 'var(--text, #0f172a)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  cursor: 'pointer',
};

const ghostBtn: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-md, 10px)',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted, #64748b)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  cursor: 'pointer',
};

export function StuckSyncModal() {
  const { db, connector } = usePowerSyncDb();
  const { user, getIdToken } = useAuth();
  const health = useSyncStatus();
  const navigate = useNavigate();

  const [dismissed, setDismissed] = useState<boolean>(isAcked);
  const [retrying, setRetrying] = useState(false);
  const [support, setSupport] = useState<{
    submitting: boolean;
    caseId: string | null;
    error: string | null;
  }>({ submitting: false, caseId: null, error: null });

  const dismiss = useCallback(() => {
    setAcked();
    setDismissed(true);
  }, []);

  const handleRetry = useCallback(async () => {
    if (!db || !connector) return;
    setRetrying(true);
    const pendingBefore = health.pendingCount;
    recordRetryLog({
      timestamp: new Date().toISOString(),
      action: 'retry',
      pendingBefore,
      outcome: 'started',
      detail: 'from stuck-sync modal',
    });
    try {
      await retrySync(db, connector);
      await new Promise((r) => setTimeout(r, 3_000));
      const after = await getUploadQueueStats(db);
      recordRetryLog({
        timestamp: new Date().toISOString(),
        action: 'retry',
        pendingBefore,
        pendingAfter: after.count,
        outcome: 'success',
        detail: after.count > 0 ? 'queue still has pending operations' : 'queue drained',
      });
    } catch (err) {
      console.error('[StuckSyncModal] Retry failed:', err);
      recordRetryLog({
        timestamp: new Date().toISOString(),
        action: 'retry',
        pendingBefore,
        outcome: 'error',
        detail: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRetrying(false);
    }
  }, [db, connector, health.pendingCount]);

  const handleAskSupport = useCallback(async () => {
    if (!db) return;
    setSupport({ submitting: true, caseId: null, error: null });
    try {
      const result = await createNeedHelpCase({
        db,
        health,
        user: { uid: user?.uid ?? null, email: user?.email ?? null },
        getIdToken: (force) => getIdToken(force),
        clinicName: import.meta.env.VITE_ORG_NAME,
      });
      setSupport({ submitting: false, caseId: result.caseId, error: null });
    } catch (err) {
      console.error('[StuckSyncModal] Ask Support failed:', err);
      setSupport({
        submitting: false,
        caseId: null,
        error: err instanceof Error ? err.message : 'Failed to send support request.',
      });
    }
  }, [db, health, user, getIdToken]);

  const handleOpenSyncStatus = useCallback(() => {
    dismiss();
    navigate(SYNC_STATUS_PATH);
  }, [dismiss, navigate]);

  // Surface when there are pending uploads and sync is not healthy — either
  // genuinely stuck (online but failing) or offline (browser has no network).
  // Both mean local changes are not yet on other devices.
  const isOffline = health.state === 'offline';
  const visible =
    !dismissed && health.pendingCount > 0 && (health.state === 'stuck' || isOffline);
  if (!visible) return null;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="stuck-sync-title">
      <div style={cardStyle}>
        <h2
          id="stuck-sync-title"
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg, 18px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            color: 'var(--text, #0f172a)',
          }}
        >
          {isOffline ? 'You’re offline' : 'Some changes haven’t synced'}
        </h2>

        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm, 14px)',
            lineHeight: 1.6,
            color: 'var(--text-muted, #475569)',
          }}
        >
          {health.pendingCount} change{health.pendingCount === 1 ? '' : 's'} saved on this device
          {' '}
          {isOffline
            ? 'have not uploaded because this device is offline, so they are not visible on other devices yet. They will upload automatically once the connection returns — your data is safe on this device in the meantime.'
            : 'have not uploaded yet, so they may not be visible on other devices. You can retry syncing, or send a support request so our team can safely inspect the pending changes before any reset.'}
        </p>

        {support.caseId && (
          <div
            style={{
              padding: 'var(--sp-3, 12px)',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 'var(--radius-md, 10px)',
              fontSize: 'var(--font-size-xs, 12px)',
              color: '#166534',
              lineHeight: 1.5,
            }}
          >
            Support request sent. Case ID: <strong>{support.caseId}</strong>. Please keep this
            device open and avoid resetting unless support asks you to proceed.
          </div>
        )}
        {support.error && (
          <div
            style={{
              padding: 'var(--sp-3, 12px)',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 'var(--radius-md, 10px)',
              fontSize: 'var(--font-size-xs, 12px)',
              color: '#991b1b',
            }}
          >
            {support.error}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3, 12px)' }}>
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            style={isOffline ? secondaryBtn : primaryBtn}
          >
            {retrying ? 'Retrying…' : 'Retry Sync'}
          </button>
          <button
            type="button"
            onClick={handleAskSupport}
            disabled={support.submitting || !!support.caseId || isOffline}
            title={isOffline ? 'Reconnect to the internet to send a support request' : undefined}
            style={{
              ...secondaryBtn,
              cursor: isOffline ? 'not-allowed' : secondaryBtn.cursor,
              opacity: isOffline ? 0.5 : 1,
            }}
          >
            {support.submitting
              ? 'Sending…'
              : support.caseId
                ? 'Request sent'
                : 'Ask Support for Help'}
          </button>
          <button type="button" onClick={handleOpenSyncStatus} style={secondaryBtn}>
            Open Sync Status
          </button>
        </div>

        {isOffline ? (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--text-muted, #94a3b8)',
            }}
          >
            Sending a support request needs an internet connection — reconnect first, then try
            again.
          </p>
        ) : (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--text-muted, #94a3b8)',
            }}
          >
            You might be offline, or the sync service may be temporarily unreachable. If you know
            you’re offline, you can skip this for now — your changes stay saved on this device.
          </p>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: isOffline ? 'flex-end' : 'space-between',
            alignItems: 'center',
            gap: 'var(--sp-3, 12px)',
          }}
        >
          {isOffline ? (
            <button type="button" onClick={dismiss} style={primaryBtn}>
              Continue working offline
            </button>
          ) : (
            <>
              <button type="button" onClick={dismiss} style={ghostBtn}>
                Skip — I’m offline
              </button>
              <button type="button" onClick={dismiss} style={ghostBtn}>
                Dismiss for now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
